import { useEffect } from 'react';
import { motion } from 'framer-motion';

export type HeaderLink = { label: string; href: string };
export type HeaderMenuSection = { label?: string; links: HeaderLink[] };

export const STATS_LINKS: HeaderLink[] = [
  { label: 'Статистика сторон', href: '/srv_stat/' },
  { label: 'Статистика отрядов', href: '/squad-stats' },
  { label: 'Статистика игроков', href: '/player-stats' },
  { label: 'Пехота', href: '/infantry' },
  { label: 'Техника', href: '/vehicles' },
  { label: 'Артиллеристы', href: '/artillery' },
  { label: 'КС', href: '/ks' },
];

export const MANUAL_CORRECTION_LINKS: HeaderLink[] = [
  { label: 'Подать заявку', href: '/manual-corrections' },
  { label: 'Реестр заявок', href: '/manual-corrections/registry' },
];

const isSpaLink = (href: string): boolean =>
  href === '/' ||
  href.startsWith('/#') ||
  href.startsWith('/player-stats') ||
  href.startsWith('/squad-stats') ||
  href.startsWith('/player/') ||
  href.startsWith('/srv_stat') ||
  href.startsWith('/achievements') ||
  href.startsWith('/heatmaps') ||
  href.startsWith('/infantry') ||
  href.startsWith('/vehicles') ||
  href.startsWith('/artillery') ||
  href.startsWith('/ks') ||
  href.startsWith('/mtsg') ||
  href.startsWith('/manual-corrections') ||
  href.startsWith('/archive');

export function HeaderNavigationMenu({
  open,
  sections,
  onClose,
  onNavigate,
}: {
  open: boolean;
  sections: HeaderMenuSection[];
  onClose: () => void;
  onNavigate: (href: string) => void;
}): JSX.Element {
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  let itemIndex = 0;

  return (
    <motion.div
      aria-hidden={!open}
      initial={{ opacity: 0, y: -6, scale: 0.98 }}
      animate={open ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: -6, scale: 0.98 }}
      transition={{ duration: 0.18 }}
      className={`fixed left-4 right-4 top-16 z-50 mt-2 w-auto max-w-md overflow-x-hidden rounded-lg border border-border bg-card shadow-lg backdrop-blur lg:absolute lg:left-0 lg:right-0 lg:top-full lg:mx-auto lg:w-[92vw] ${
        open ? 'visible pointer-events-auto' : 'invisible pointer-events-none'
      }`}
    >
      <div className="max-h-80 overflow-y-auto py-2">
        {sections.map((section) => (
          <section key={section.label ?? 'links'} aria-label={section.label}>
            {section.label ? (
              <p className="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {section.label}
              </p>
            ) : null}
            {section.links.map((item) => {
              const animationIndex = itemIndex;
              itemIndex += 1;
              return (
                <motion.a
                  key={item.label}
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel={item.href.startsWith('http') ? 'noreferrer' : undefined}
                  tabIndex={open ? 0 : -1}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground transition hover:bg-accent/30"
                  initial={{ opacity: 0, y: -4 }}
                  animate={open ? { opacity: 1, y: 0 } : { opacity: 0, y: -4 }}
                  transition={{ duration: 0.12, delay: open ? animationIndex * 0.02 : 0 }}
                  onClick={(event) => {
                    if (isSpaLink(item.href)) {
                      event.preventDefault();
                      onNavigate(item.href);
                    }
                    onClose();
                  }}
                >
                  {item.label}
                </motion.a>
              );
            })}
          </section>
        ))}
      </div>
    </motion.div>
  );
}
